// src/pages/tiktok.tsx
import React, { useState, useEffect, useRef, useCallback, memo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import ReactPlayer from "react-player/lazy";
import { useInView } from "react-intersection-observer";
import { getSubredditPosts } from "../api/redditApi";
import { RedditPost } from "../types/reddit";
import axios from "axios";

// --- Helper Functions (Unchanged) ---
function decodeHtmlEntities(text: string | undefined | null): string {
  if (!text) return "";
  try {
    const textarea = document.createElement("textarea");
    textarea.innerHTML = text;
    return textarea.value || text;
  } catch (e) {
    console.warn("Could not decode HTML entities:", text, e);
    return text
      .replace(/</g, "<")
      .replace(/>/g, ">")
      .replace(/"/g, '"')
      .replace(/'/g, "'")
      .replace(/&/g, "&");
  }
}
function getPreviewImageUrl(post: RedditPost, minWidth = 600): string | null {
  const previews = post.preview?.images?.[0]?.resolutions;
  if (!previews || previews.length === 0) {
    const sourceUrl = post.preview?.images?.[0]?.source?.url;
    if (sourceUrl) return decodeHtmlEntities(sourceUrl);
    if (post.thumbnail && post.thumbnail.startsWith("http")) {
      return decodeHtmlEntities(post.thumbnail);
    }
    return null;
  }
  let bestUrl = decodeHtmlEntities(previews[previews.length - 1]!.url);
  for (let i = previews.length - 1; i >= 0; i--) {
    if (previews[i]!.width >= minWidth) {
      bestUrl = decodeHtmlEntities(previews[i]!.url);
    } else {
      break;
    }
  }
  return bestUrl;
}
function getVideoUrl(post: RedditPost): string | null {
  const redditVideo = post.media?.reddit_video;
  const previewVideo = post.preview?.reddit_video_preview;
  if (redditVideo?.dash_url) {
    return decodeHtmlEntities(redditVideo.dash_url);
  }
  if (redditVideo?.hls_url) {
    return decodeHtmlEntities(redditVideo.hls_url);
  }
  if (post.is_video && redditVideo?.fallback_url) {
    return decodeHtmlEntities(redditVideo.fallback_url.split("?")[0]);
  }
  if (previewVideo?.dash_url) {
    return decodeHtmlEntities(previewVideo.dash_url);
  }
  if (previewVideo?.hls_url) {
    return decodeHtmlEntities(previewVideo.hls_url);
  }
  if (previewVideo?.fallback_url) {
    return decodeHtmlEntities(previewVideo.fallback_url.split("?")[0]);
  }
  return null;
}
function getSingleImageUrl(post: RedditPost): string | null {
  if (
    post.post_hint === "image" ||
    /\.(jpg|jpeg|png|gif|webp)$/i.test(decodeHtmlEntities(post.url))
  ) {
    return getPreviewImageUrl(post, 1080) || decodeHtmlEntities(post.url);
  }
  return null;
}
function getGalleryImageUrls(
  post: RedditPost
): { url: string | null; caption?: string }[] {
  if (!post.is_gallery || !post.gallery_data?.items || !post.media_metadata)
    return [];
  return post.gallery_data.items
    .map((item) => {
      const meta = post.media_metadata?.[item.media_id];
      let imageUrl: string | null = null;
      if (meta && meta.status === "valid") {
        const sourceUrl = meta.s?.u || meta.s?.gif;
        const largestPreviewUrl = meta.p?.[meta.p.length - 1]?.u;
        imageUrl = decodeHtmlEntities(sourceUrl || largestPreviewUrl || null);
      }
      return { url: imageUrl, caption: decodeHtmlEntities(item.caption) };
    })
    .filter((item) => item.url);
}
function getGfycatRedgifsEmbedUrl(url: string): string | null {
  try {
    const parsedUrl = new URL(url);
    const hostname = parsedUrl.hostname.toLowerCase();
    const pathSegments = parsedUrl.pathname.split("/").filter(Boolean);
    if (
      (hostname.includes("gfycat.com") || hostname.includes("redgifs.com")) &&
      pathSegments.length > 0
    ) {
      const slug = pathSegments[pathSegments.length - 1];
      const embedHost = "redgifs.com";
      if (slug && /^[a-zA-Z0-9-_]+$/.test(slug)) {
        return `https://www.${embedHost}/ifr/${slug}?autoplay=1&hd=1&controls=0&click_to_play=0&sound=0&muted=1`;
      }
    }
  } catch (e) {
    /* Ignore invalid URLs */
  }
  return null;
}
function getEmbedData(
  post: RedditPost
): { type: "iframe_url" | "url"; content: string } | null {
  const decodedUrl = decodeHtmlEntities(post.url);
  const gfycatRedgifsUrl = getGfycatRedgifsEmbedUrl(decodedUrl);
  if (gfycatRedgifsUrl) {
    return { type: "iframe_url", content: gfycatRedgifsUrl };
  }
  if (post.media?.oembed?.html && post.post_hint === "rich:video") {
    const decodedEmbedHtml = decodeHtmlEntities(post.media.oembed.html);
    if (decodedEmbedHtml.trim().startsWith("<iframe")) {
      const srcMatch = decodedEmbedHtml.match(/src="([^"]+)"/);
      if (srcMatch?.[1]) {
        let embedSrc = decodeHtmlEntities(srcMatch[1]);
        try {
          const urlObj = new URL(embedSrc);
          if (
            urlObj.hostname.includes("youtube.com") ||
            urlObj.hostname.includes("youtu.be") ||
            urlObj.hostname.includes("vimeo.com")
          ) {
            urlObj.searchParams.set("autoplay", "1");
            urlObj.searchParams.set("mute", "1");
            if (
              urlObj.hostname.includes("youtube.com") ||
              urlObj.hostname.includes("youtu.be")
            ) {
              urlObj.searchParams.set("enablejsapi", "1");
              urlObj.searchParams.set("origin", window.location.origin);
            }
            embedSrc = urlObj.toString();
          }
        } catch (e) {
          /* Ignore */
        }
        return { type: "iframe_url", content: embedSrc };
      }
    }
  }
  const isKnownVideoHost =
    /youtube\.com|youtu\.be|vimeo\.com|streamable\.com|dailymotion\.com|twitch\.tv/i.test(
      post.domain
    );
  if (
    (post.post_hint === "link" ||
      post.post_hint === "rich:video" ||
      isKnownVideoHost) &&
    ReactPlayer.canPlay(decodedUrl) &&
    !/gfycat\.com|redgifs\.com/i.test(post.domain)
  ) {
    return { type: "url", content: decodedUrl };
  }
  return null;
}
// --- END Helper Functions ---

// --- Types ---
interface TikTokPost extends RedditPost {
  mediaType: "video" | "image" | "gallery" | "embed" | "unsupported";
  mediaContent: any;
}
type SortOption = "hot" | "new" | "top" | "rising";
type TimeframeOption = "hour" | "day" | "week" | "month" | "year" | "all";
interface Filters {
  showVideos: boolean;
  showImages: boolean;
  showEmbeds: boolean;
}

// --- Constants ---
const DEFAULT_SUBREDDIT = "aww";
const POST_LIMIT = 10;
const OBSERVER_THRESHOLD = 0.7;

// --- Components ---

interface PostSlideProps {
  post: TikTokPost;
  onVisible: (postId: string) => void;
  isActive: boolean;
  isGloballyMuted: boolean;
  toggleGlobalMute: () => void;
}

const PostSlide: React.FC<PostSlideProps> = memo(
  ({ post, onVisible, isActive, isGloballyMuted, toggleGlobalMute }) => {
    const [galleryIndex, setGalleryIndex] = useState(0);
    const playerRef = useRef<ReactPlayer>(null);
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const [isMediaLoading, setIsMediaLoading] = useState(true);
    const loadingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
      null
    );

    const { ref: slideRef } = useInView({
      threshold: OBSERVER_THRESHOLD,
      onChange: (inView) => {
        if (inView) {
          onVisible(post.id);
        }
      },
    });

    useEffect(() => {
      setGalleryIndex(0);
      setIsMediaLoading(true);
      if (loadingTimeoutRef.current !== null)
        clearTimeout(loadingTimeoutRef.current);
      loadingTimeoutRef.current = setTimeout(() => {
        if (isMediaLoading) setIsMediaLoading(false);
      }, 8000);
      return () => {
        if (loadingTimeoutRef.current) clearTimeout(loadingTimeoutRef.current);
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [post.id]);

    useEffect(() => {
      const playDelay = 150;
      let playTimeoutId: ReturnType<typeof setTimeout> | null = null;
      if (isActive) {
        playTimeoutId = setTimeout(() => {
          if (playerRef.current) {
            playerRef.current.seekTo(0, "seconds");
            const internalPlayer = playerRef.current.getInternalPlayer();
            if (internalPlayer && typeof internalPlayer.play === "function") {
              internalPlayer
                .play()
                .catch((e: any) =>
                  console.warn(`Autoplay prevented for ${post.id}:`, e)
                );
            }
          }
          if (iframeRef.current?.contentWindow) {
            iframeRef.current.contentWindow.postMessage(
              '{"event":"command","func":"playVideo","args":""}',
              "*"
            );
            iframeRef.current.contentWindow.postMessage(
              '{"method":"play"}',
              "*"
            );
          }
        }, playDelay);
      } else {
        if (playerRef.current) {
          const internalPlayer = playerRef.current.getInternalPlayer();
          if (internalPlayer && typeof internalPlayer.pause === "function") {
            internalPlayer.pause();
          }
        }
        if (iframeRef.current?.contentWindow) {
          try {
            iframeRef.current.contentWindow.postMessage(
              '{"event":"command","func":"pauseVideo","args":""}',
              "*"
            );
            iframeRef.current.contentWindow.postMessage(
              '{"method":"pause"}',
              "*"
            );
          } catch (e) {
            /* Ignore */
          }
        }
      }
      return () => {
        if (playTimeoutId) clearTimeout(playTimeoutId);
      };
    }, [isActive, post.id]);

    const handleGalleryNext = (e: React.MouseEvent) => {
      e.stopPropagation();
      setGalleryIndex((p) => (p + 1) % post.mediaContent.length);
      setIsMediaLoading(true);
    };
    const handleGalleryPrev = (e: React.MouseEvent) => {
      e.stopPropagation();
      setGalleryIndex(
        (p) => (p - 1 + post.mediaContent.length) % post.mediaContent.length
      );
      setIsMediaLoading(true);
    };
    const handleMediaClick = () => {
      if (post.mediaType === "video" || post.mediaType === "embed")
        toggleGlobalMute();
    };
    const handleMuteToggle = (e: React.MouseEvent) => {
      e.stopPropagation();
      toggleGlobalMute();
    };

    const handleMediaLoaded = useCallback(() => {
      if (loadingTimeoutRef.current !== null)
        clearTimeout(loadingTimeoutRef.current);
      setIsMediaLoading(false);
    }, []);
    const handleMediaError = useCallback(
      (errorType: string, error?: any) => {
        console.error(
          `${errorType} Error:`,
          error,
          `Post: ${post.permalink}`,
          post.mediaContent
        );
        if (loadingTimeoutRef.current !== null)
          clearTimeout(loadingTimeoutRef.current);
        setIsMediaLoading(false);
      },
      [post.permalink, post.mediaContent]
    );

    const renderMedia = () => {
      const commonWrapperClasses =
        "absolute inset-0 flex items-center justify-center bg-black overflow-hidden";
      const mediaTransitionClasses =
        "transition-opacity duration-300 ease-in-out";

      switch (post.mediaType) {
        case "video":
          return (
            <div className={commonWrapperClasses} onClick={handleMediaClick}>
              <ReactPlayer
                ref={playerRef}
                url={post.mediaContent}
                playing={isActive}
                loop={true}
                controls={false}
                width="100%"
                height="100%"
                playsinline
                muted={isGloballyMuted}
                onReady={handleMediaLoaded}
                onBufferEnd={handleMediaLoaded}
                onError={(e, data) =>
                  handleMediaError("ReactPlayer Video", { e, data })
                }
                className={`react-player ${mediaTransitionClasses}`}
                style={{ objectFit: "contain" }}
                config={{
                  file: {
                    attributes: { preload: isActive ? "auto" : "metadata" },
                    forceHLS: post.mediaContent?.includes(".m3u8"),
                    forceDASH: post.mediaContent?.includes(".mpd"),
                  },
                }}
              />
              {isActive && !isMediaLoading && (
                <div className="absolute text-white bg-black/30 rounded-full p-2 pointer-events-none z-10 opacity-80">
                  {isGloballyMuted ? (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M9.383 3.076A1 1 0 0110 4v12a1 1 0 01-1.707.707L4.586 13H2a1 1 0 01-1-1V8a1 1 0 011-1h2.586l3.707-3.707a1 1 0 011.09-.217zM12.293 7.293a1 1 0 011.414 0L15 8.586l1.293-1.293a1 1 0 111.414 1.414L16.414 10l1.293 1.293a1 1 0 01-1.414 1.414L15 11.414l-1.293 1.293a1 1 0 01-1.414-1.414L13.586 10l-1.293-1.293a1 1 0 010-1.414z"
                        clipRule="evenodd"
                      />
                    </svg>
                  ) : (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M9.383 3.076A1 1 0 0110 4v12a1 1 0 01-1.707.707L4.586 13H2a1 1 0 01-1-1V8a1 1 0 011-1h2.586l3.707-3.707a1 1 0 011.09-.217zM14.657 2.929a1 1 0 011.414 0A9.972 9.972 0 0119 10a9.972 9.972 0 01-2.929 7.071 1 1 0 01-1.414-1.414A7.971 7.971 0 0017 10c0-2.21-.894-4.208-2.343-5.657a1 1 0 010-1.414zm-2.829 2.828a1 1 0 011.415 0A5.983 5.983 0 0115 10a5.984 5.984 0 01-1.757 4.243 1 1 0 01-1.415-1.415A3.984 3.984 0 0013 10a3.983 3.983 0 00-1.172-2.828 1 1 0 010-1.414z"
                        clipRule="evenodd"
                      />
                    </svg>
                  )}
                </div>
              )}
            </div>
          );
        case "image":
          return (
            <div className={commonWrapperClasses}>
              <img
                src={post.mediaContent}
                alt={post.title}
                className={`w-full h-full object-contain ${mediaTransitionClasses}`}
                loading={isActive ? "eager" : "lazy"}
                onLoad={handleMediaLoaded}
                onError={(e) => handleMediaError("Image", e)}
              />
            </div>
          );
        case "gallery":
          const currentItem = post.mediaContent[galleryIndex];
          return (
            <div className={commonWrapperClasses}>
              {currentItem?.url ? (
                <img
                  src={currentItem.url}
                  alt={currentItem.caption || post.title}
                  className={`w-full h-full object-contain ${mediaTransitionClasses}`}
                  key={currentItem.url + galleryIndex}
                  loading={isActive ? "eager" : "lazy"}
                  onLoad={handleMediaLoaded}
                  onError={(e) => handleMediaError("Gallery Image", e)}
                />
              ) : (
                <div className="text-white text-center p-4">
                  Image not available
                </div>
              )}
              {post.mediaContent.length > 1 && !isMediaLoading && (
                <>
                  {" "}
                  <button
                    onClick={handleGalleryPrev}
                    aria-label="Previous image"
                    className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-black/30 text-white p-1.5 rounded-full z-10 focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M15 19l-7-7 7-7"
                      />
                    </svg>
                  </button>{" "}
                  <button
                    onClick={handleGalleryNext}
                    aria-label="Next image"
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-black/30 text-white p-1.5 rounded-full z-10 focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  </button>{" "}
                  <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 bg-black/40 px-2 py-0.5 rounded-full text-[10px] z-10 pointer-events-none">
                    {galleryIndex + 1} / {post.mediaContent.length}
                  </div>{" "}
                </>
              )}
            </div>
          );
        case "embed":
          const embedData = post.mediaContent as {
            type: "iframe_url" | "url";
            content: string;
          };
          if (embedData.type === "iframe_url") {
            return (
              <div className={commonWrapperClasses} onClick={handleMediaClick}>
                {" "}
                <iframe
                  ref={iframeRef}
                  key={post.id + embedData.content + isActive}
                  src={embedData.content}
                  frameBorder="0"
                  scrolling="no"
                  allowFullScreen
                  allow="autoplay; fullscreen; encrypted-media; picture-in-picture; web-share;"
                  sandbox="allow-scripts allow-same-origin allow-presentation allow-popups allow-forms allow-autoplay"
                  className={`w-full h-full ${mediaTransitionClasses}`}
                  title={`${post.domain} embed`}
                  loading={isActive ? "eager" : "lazy"}
                  onLoad={handleMediaLoaded}
                  onError={(e) => handleMediaError("Iframe", e)}
                ></iframe>{" "}
                {isActive && !isMediaLoading && (
                  <div className="absolute text-white bg-black/30 rounded-full p-2 pointer-events-none z-10 opacity-80">
                    {isGloballyMuted ? (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M9.383 3.076A1 1 0 0110 4v12a1 1 0 01-1.707.707L4.586 13H2a1 1 0 01-1-1V8a1 1 0 011-1h2.586l3.707-3.707a1 1 0 011.09-.217zM12.293 7.293a1 1 0 011.414 0L15 8.586l1.293-1.293a1 1 0 111.414 1.414L16.414 10l1.293 1.293a1 1 0 01-1.414 1.414L15 11.414l-1.293 1.293a1 1 0 01-1.414-1.414L13.586 10l-1.293-1.293a1 1 0 010-1.414z"
                          clipRule="evenodd"
                        />
                      </svg>
                    ) : (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M9.383 3.076A1 1 0 0110 4v12a1 1 0 01-1.707.707L4.586 13H2a1 1 0 01-1-1V8a1 1 0 011-1h2.586l3.707-3.707a1 1 0 011.09-.217zM14.657 2.929a1 1 0 011.414 0A9.972 9.972 0 0119 10a9.972 9.972 0 01-2.929 7.071 1 1 0 01-1.414-1.414A7.971 7.971 0 0017 10c0-2.21-.894-4.208-2.343-5.657a1 1 0 010-1.414zm-2.829 2.828a1 1 0 011.415 0A5.983 5.983 0 0115 10a5.984 5.984 0 01-1.757 4.243 1 1 0 01-1.415-1.415A3.984 3.984 0 0013 10a3.983 3.983 0 00-1.172-2.828 1 1 0 010-1.414z"
                          clipRule="evenodd"
                        />
                      </svg>
                    )}
                  </div>
                )}{" "}
              </div>
            );
          } else {
            return (
              <div className={commonWrapperClasses} onClick={handleMediaClick}>
                {" "}
                <ReactPlayer
                  ref={playerRef}
                  url={embedData.content}
                  playing={isActive}
                  loop={true}
                  controls={false}
                  width="100%"
                  height="100%"
                  playsinline
                  muted={isGloballyMuted}
                  onReady={handleMediaLoaded}
                  onBufferEnd={handleMediaLoaded}
                  onError={(e) => handleMediaError("ReactPlayer Embed", e)}
                  className={`react-player ${mediaTransitionClasses}`}
                  style={{ objectFit: "contain" }}
                  config={{
                    file: {
                      attributes: { preload: isActive ? "auto" : "metadata" },
                    },
                  }}
                />{" "}
                {isActive && !isMediaLoading && (
                  <div className="absolute text-white bg-black/30 rounded-full p-2 pointer-events-none z-10 opacity-80">
                    {isGloballyMuted ? (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M9.383 3.076A1 1 0 0110 4v12a1 1 0 01-1.707.707L4.586 13H2a1 1 0 01-1-1V8a1 1 0 011-1h2.586l3.707-3.707a1 1 0 011.09-.217zM12.293 7.293a1 1 0 011.414 0L15 8.586l1.293-1.293a1 1 0 111.414 1.414L16.414 10l1.293 1.293a1 1 0 01-1.414 1.414L15 11.414l-1.293 1.293a1 1 0 01-1.414-1.414L13.586 10l-1.293-1.293a1 1 0 010-1.414z"
                          clipRule="evenodd"
                        />
                      </svg>
                    ) : (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M9.383 3.076A1 1 0 0110 4v12a1 1 0 01-1.707.707L4.586 13H2a1 1 0 01-1-1V8a1 1 0 011-1h2.586l3.707-3.707a1 1 0 011.09-.217zM14.657 2.929a1 1 0 011.414 0A9.972 9.972 0 0119 10a9.972 9.972 0 01-2.929 7.071 1 1 0 01-1.414-1.414A7.971 7.971 0 0017 10c0-2.21-.894-4.208-2.343-5.657a1 1 0 010-1.414zm-2.829 2.828a1 1 0 011.415 0A5.983 5.983 0 0115 10a5.984 5.984 0 01-1.757 4.243 1 1 0 01-1.415-1.415A3.984 3.984 0 0013 10a3.983 3.983 0 00-1.172-2.828 1 1 0 010-1.414z"
                          clipRule="evenodd"
                        />
                      </svg>
                    )}
                  </div>
                )}{" "}
              </div>
            );
          }
        default:
          return (
            <div
              className={`${commonWrapperClasses} text-white p-4 text-center`}
            >
              Unsupported content type
              <br />
              <span className="text-xs opacity-60">{post.domain}</span>
            </div>
          );
      }
    };

    return (
      <div
        ref={slideRef}
        className="w-full h-full relative text-white snap-start flex-shrink-0"
      >
        <div className="w-full h-full relative">
          {renderMedia()}
          {isMediaLoading && isActive && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/60 z-20 pointer-events-none">
              {" "}
              <svg
                className="animate-spin h-8 w-8 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>{" "}
            </div>
          )}
        </div>
        <div
          className={`absolute inset-0 flex flex-col justify-between p-4 pointer-events-none transition-opacity duration-300 ease-in-out ${
            isMediaLoading && isActive ? "opacity-0" : "opacity-100"
          }`}
        >
          <div></div>
          <div className="pb-10 z-10">
            <div className="absolute bottom-0 left-0 right-0 h-1/3 bg-gradient-to-t from-black/70 via-black/40 to-transparent pointer-events-none"></div>
            <div className="relative z-10 pointer-events-auto">
              <h2 className="text-lg font-bold mb-1 line-clamp-2 drop-shadow-md">
                {post.title}
              </h2>
              <div className="text-xs opacity-80 mb-2 space-x-2 drop-shadow">
                <span>{post.subreddit_name_prefixed}</span>
                <span>•</span>
                <span>u/{post.author}</span>
                <span>•</span>
                <span>{post.score} pts</span>
              </div>
              <a
                href={`https://www.reddit.com${post.permalink}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center text-xs bg-white/20 hover:bg-white/30 text-white px-2 py-1 rounded-full transition-colors duration-150 drop-shadow"
                onClick={(e) => e.stopPropagation()}
              >
                {" "}
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-3.5 w-3.5 mr-1"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M18 10c0 3.866-3.582 7-8 7a8.74 8.74 0 01-4.246-.986l.976.976 1.894-1.894-.976-.976A8.74 8.74 0 012 10c0-3.866 3.582-7 8-7s8 3.134 8 7zM7 9a1 1 0 11-2 0 1 1 0 012 0zm7 0a1 1 0 11-2 0 1 1 0 012 0zM5.002 12.155a1 1 0 01.8-.4h8.396a1 1 0 01.8.4l.02.021a5.71 5.71 0 01-4.618 2.59 5.71 5.71 0 01-4.618-2.59l.02-.021z"
                    clipRule="evenodd"
                  />
                </svg>{" "}
                {post.num_comments} Comments{" "}
              </a>
              {post.mediaType === "gallery" &&
                post.mediaContent[galleryIndex]?.caption && (
                  <p className="text-xs mt-2 opacity-80 line-clamp-2 drop-shadow">
                    {post.mediaContent[galleryIndex].caption}
                  </p>
                )}
            </div>
          </div>
          {(post.mediaType === "video" || post.mediaType === "embed") && (
            <div className="absolute right-2 bottom-24 flex flex-col items-center space-y-4 pointer-events-auto z-10">
              <button
                onClick={handleMuteToggle}
                className="p-2 bg-black/30 rounded-full text-white hover:bg-black/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
                aria-label={isGloballyMuted ? "Unmute" : "Mute"}
              >
                {isGloballyMuted ? (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M9.383 3.076A1 1 0 0110 4v12a1 1 0 01-1.707.707L4.586 13H2a1 1 0 01-1-1V8a1 1 0 011-1h2.586l3.707-3.707a1 1 0 011.09-.217zM12.293 7.293a1 1 0 011.414 0L15 8.586l1.293-1.293a1 1 0 111.414 1.414L16.414 10l1.293 1.293a1 1 0 01-1.414 1.414L15 11.414l-1.293 1.293a1 1 0 01-1.414-1.414L13.586 10l-1.293-1.293a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                ) : (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M9.383 3.076A1 1 0 0110 4v12a1 1 0 01-1.707.707L4.586 13H2a1 1 0 01-1-1V8a1 1 0 011-1h2.586l3.707-3.707a1 1 0 011.09-.217zM14.657 2.929a1 1 0 011.414 0A9.972 9.972 0 0119 10a9.972 9.972 0 01-2.929 7.071 1 1 0 01-1.414-1.414A7.971 7.971 0 0017 10c0-2.21-.894-4.208-2.343-5.657a1 1 0 010-1.414zm-2.829 2.828a1 1 0 011.415 0A5.983 5.983 0 0115 10a5.984 5.984 0 01-1.757 4.243 1 1 0 01-1.415-1.415A3.984 3.984 0 0013 10a3.983 3.983 0 00-1.172-2.828 1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }
);
PostSlide.displayName = "PostSlide";

// --- Main Page Component ---

const SecretTikTokPage: React.FC = () => {
  const { subredditName: initialSubreddit } = useParams<{
    subredditName?: string;
  }>();
  const navigate = useNavigate();
  const [posts, setPosts] = useState<TikTokPost[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [after, setAfter] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [subreddit, setSubreddit] = useState<string>(
    initialSubreddit?.replace(/^r\//i, "") || DEFAULT_SUBREDDIT
  );
  const [sort, setSort] = useState<SortOption>("hot");
  const [timeframe, setTimeframe] = useState<TimeframeOption>("day");
  const [filters, setFilters] = useState<Filters>({
    showVideos: true,
    showImages: true,
    showEmbeds: true,
  });
  const [isSettingsOpen, setIsSettingsOpen] = useState<boolean>(false);
  const [activePostId, setActivePostId] = useState<string | null>(null);
  const [isGloballyMuted, setIsGloballyMuted] = useState<boolean>(true);

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const isFetchingMore = useRef<boolean>(false); // Ref to prevent concurrent fetches

  // --- API Fetching and Processing ---
  const processRedditPost = useCallback(
    (post: RedditPost): TikTokPost | null => {
      let mediaType: TikTokPost["mediaType"] = "unsupported";
      let mediaContent: TikTokPost["mediaContent"] = null;
      const videoUrl = getVideoUrl(post);
      const imageUrl = getSingleImageUrl(post);
      const galleryUrls = getGalleryImageUrls(post);
      const embedData = getEmbedData(post);
      if (videoUrl && filters.showVideos) {
        mediaType = "video";
        mediaContent = videoUrl;
      } else if (galleryUrls.length > 0 && filters.showImages) {
        mediaType = "gallery";
        mediaContent = galleryUrls;
      } else if (imageUrl && filters.showImages) {
        mediaType = "image";
        mediaContent = imageUrl;
      } else if (embedData && filters.showEmbeds) {
        mediaType = "embed";
        mediaContent = embedData;
      }
      return mediaType !== "unsupported"
        ? { ...post, mediaType, mediaContent }
        : null;
    },
    [filters]
  );

  const fetchPosts = useCallback(
    async (loadAfter: string | null = null, isRetry = false) => {
      // Prevent fetch if already fetching (ref check), no subreddit, initial load ongoing, or no more posts (unless retrying)
      if (
        isFetchingMore.current ||
        !subreddit ||
        (!loadAfter && isLoading) ||
        (loadAfter && !hasMore && !isRetry)
      ) {
        return;
      }
      isFetchingMore.current = true; // Set ref immediately
      if (loadAfter) setIsLoadingMore(true);
      else setIsLoading(true);
      if (!loadAfter || isRetry) setError(null);

      try {
        const params: Record<string, any> = { limit: POST_LIMIT };
        if (loadAfter) params.after = loadAfter;
        if (sort === "top") params.t = timeframe;
        const response = await getSubredditPosts(subreddit, sort, params);
        const fetchedRedditPosts = response?.data?.children || [];
        const processed = fetchedRedditPosts
          .map((c: { data: RedditPost }) => processRedditPost(c.data))
          .filter((p: TikTokPost | null): p is TikTokPost => p !== null);
        const newAfter = response?.data?.after;
        const currentHasMore = !!newAfter;

        // Use functional updates for state based on previous state
        setHasMore(currentHasMore);
        setAfter(newAfter);
        setPosts((prev) => {
          const existingIds = new Set(prev.map((p) => p.id));
          const uniqueNew = processed.filter(
            (p: TikTokPost) => !existingIds.has(p.id)
          );
          const combined = loadAfter ? [...prev, ...uniqueNew] : processed;
          // Set initial active post only if it's the first load, we got posts, and activePostId is still null
          if (!loadAfter && combined.length > 0 && activePostId === null) {
            // Use timeout to ensure DOM update before setting active ID (might help with initial scroll)
            setTimeout(() => setActivePostId(combined[0].id), 0);
          }
          return combined;
        });

        // Auto-fetch next if first page was empty but API has more
        if (!loadAfter && processed.length === 0 && currentHasMore) {
          console.log(
            `Initial fetch for r/${subreddit} empty, fetching next...`
          );
          isFetchingMore.current = false; // Allow next fetch immediately
          // Keep isLoading true for the overall initial load phase
          fetchPosts(newAfter);
          return; // Exit early to avoid double finally block
        }
      } catch (err: any) {
        console.error(`Fetch error r/${subreddit}:`, err);
        if (err.name !== "CanceledError" && !axios.isCancel(err)) {
          if (axios.isAxiosError(err)) {
            if (err.response?.status === 404)
              setError(`Subreddit r/${subreddit} not found or private.`);
            else if (err.response?.status === 403)
              setError(`Access denied to r/${subreddit}.`);
            else
              setError(
                `Failed to load posts: Server error ${err.response?.status}`
              );
          } else {
            setError(`Failed to load posts. ${err.message}`);
          }
          if (!loadAfter) setPosts([]); // Clear posts only on initial load error
          setHasMore(false); // Stop pagination on any error
        }
      } finally {
        // Reset flags in finally block
        if (!loadAfter) setIsLoading(false);
        setIsLoadingMore(false);
        isFetchingMore.current = false; // Allow next fetch attempt
      }
    },
    [
      subreddit,
      sort,
      timeframe,
      processRedditPost,
      isLoading,
      hasMore,
      activePostId,
      filters,
    ]
  ); // Added filters dependency via processRedditPost

  // Effect for Initial fetch and refetch on settings change
  useEffect(() => {
    setPosts([]);
    setAfter(null);
    setHasMore(true);
    setActivePostId(null);
    const timer = setTimeout(() => {
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollTo({ top: 0, behavior: "auto" });
      }
    }, 50);
    fetchPosts(null);
    if (
      subreddit !==
      (initialSubreddit?.replace(/^r\//i, "") || DEFAULT_SUBREDDIT)
    ) {
      navigate(`/tiktok/${subreddit}`, { replace: true });
    }
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subreddit, sort, timeframe, filters]); // Dependencies: settings that trigger refetch

  // --- Infinite Scroll Trigger ---
  const { ref: loadMoreTriggerRef, inView: loadMoreInView } = useInView({
    threshold: 0,
    rootMargin: "400px 0px 0px 0px",
  }); // Increased rootMargin
  useEffect(() => {
    // Trigger fetch only if:
    // - Trigger is in view
    // - Initial load is complete (posts.length > 0)
    // - Not currently loading initial OR more posts
    // - API indicates more posts are available
    // - We have a valid 'after' token
    if (
      loadMoreInView &&
      posts.length > 0 &&
      !isLoading &&
      !isLoadingMore &&
      hasMore &&
      after
    ) {
      // Double check ref to prevent race condition during fetch
      if (!isFetchingMore.current) {
        fetchPosts(after);
      }
    }
  }, [
    loadMoreInView,
    posts.length,
    isLoading,
    isLoadingMore,
    hasMore,
    after,
    fetchPosts,
  ]);

  // --- Settings Menu Logic ---
  const handleSettingsSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const newSubreddit =
      (formData.get("subreddit") as string) || DEFAULT_SUBREDDIT;
    const newSort = formData.get("sort") as SortOption;
    const newTimeframe = formData.get("timeframe") as TimeframeOption;
    const newFilters: Filters = {
      showVideos: formData.get("showVideos") === "on",
      showImages: formData.get("showImages") === "on",
      showEmbeds: formData.get("showEmbeds") === "on",
    };
    const cleanSubreddit = newSubreddit.trim().replace(/^r\//i, "");
    if (
      cleanSubreddit !== subreddit ||
      newSort !== sort ||
      newTimeframe !== timeframe ||
      JSON.stringify(newFilters) !== JSON.stringify(filters)
    ) {
      setSubreddit(cleanSubreddit);
      setSort(newSort);
      setTimeframe(newTimeframe);
      setFilters(newFilters);
    }
    setIsSettingsOpen(false);
  };

  // --- Active Slide Update Callback ---
  const handleSlideVisible = useCallback((postId: string) => {
    // No need to debounce here, direct update is fine
    setActivePostId(postId);
  }, []);

  // --- Global Mute Toggle Function ---
  const toggleGlobalMute = useCallback(() => {
    setIsGloballyMuted((prev) => !prev);
  }, []);

  // --- Render ---
  return (
    <div className="fixed inset-0 bg-black flex flex-col overflow-hidden">
      {/* Scroll Container */}
      <div
        ref={scrollContainerRef}
        className="w-full h-full overflow-y-auto overflow-x-hidden snap-y snap-mandatory scroll-smooth"
        style={
          {
            scrollbarWidth: "none",
            msOverflowStyle: "none",
          } as React.CSSProperties
        }
      >
        {/* Render Posts */}
        {posts.map((post) => (
          <PostSlide
            key={post.id}
            post={post}
            onVisible={handleSlideVisible}
            isActive={post.id === activePostId}
            isGloballyMuted={isGloballyMuted}
            toggleGlobalMute={toggleGlobalMute}
          />
        ))}
        {/* Load More Trigger & Indicator */}
        <div
          ref={loadMoreTriggerRef}
          className="h-20 flex-shrink-0 snap-start flex items-center justify-center"
        >
          {isLoadingMore && (
            <div className="flex items-center space-x-2 text-white/50 text-xs">
              {" "}
              <svg
                className="animate-spin h-4 w-4"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>{" "}
              <span>Loading...</span>{" "}
            </div>
          )}
          {!hasMore && !isLoading && !isLoadingMore && posts.length > 0 && (
            <div className="text-white/50 text-xs">End of feed.</div>
          )}
        </div>
      </div>
      {/* Initial Loading Spinner */}
      {isLoading && posts.length === 0 && !error && (
        <div className="absolute inset-0 flex items-center justify-center text-white bg-black/70 z-50 pointer-events-none">
          {" "}
          <svg
            className="animate-spin h-10 w-10 text-white"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            ></circle>
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>{" "}
        </div>
      )}
      {/* Error Overlay */}
      {error && (
        <div className="absolute inset-0 z-40 flex flex-col items-center justify-center text-center text-red-400 bg-black/80 p-4">
          {" "}
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-12 w-12 text-red-500 mb-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={1.5}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>{" "}
          <p className="font-semibold mb-2">Error Loading Feed</p>{" "}
          <p className="text-sm mb-4">{error}</p>{" "}
          <div className="flex space-x-4">
            {" "}
            <button
              onClick={() => fetchPosts(null, true)}
              className="px-4 py-1.5 bg-red-600/50 hover:bg-red-500/50 text-white rounded text-sm"
            >
              Retry
            </button>{" "}
            <button
              onClick={() => setIsSettingsOpen(true)}
              className="px-4 py-1.5 bg-gray-600/50 hover:bg-gray-500/50 text-white rounded text-sm"
            >
              Settings
            </button>{" "}
          </div>{" "}
        </div>
      )}
      {/* No Posts Found Overlay */}
      {!isLoading && !error && posts.length === 0 && (
        <div className="absolute inset-0 z-40 flex flex-col items-center justify-center text-center text-gray-400 bg-black/80 p-4">
          {" "}
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-12 w-12 text-gray-500 mb-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={1.5}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>{" "}
          <p className="font-semibold mb-2">No Posts Found</p>{" "}
          <p className="text-sm mb-4">
            No posts matching the current filters were found in r/{subreddit}.
            <br />
            Try adjusting the filters or subreddit.
          </p>{" "}
          <button
            onClick={() => setIsSettingsOpen(true)}
            className="mt-2 px-4 py-1.5 bg-gray-600/50 hover:bg-gray-500/50 text-white rounded text-sm"
          >
            Open Settings
          </button>{" "}
        </div>
      )}
      {/* UI Buttons */}
      <button
        onClick={() => setIsSettingsOpen(true)}
        className="absolute top-4 right-4 z-30 p-2 bg-black/30 hover:bg-black/50 rounded-full text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
        aria-label="Open Settings"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-5 w-5"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
          />
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
          />
        </svg>
      </button>
      <button
        onClick={() => navigate(-1)}
        className="absolute top-4 left-4 z-30 p-2 bg-black/30 hover:bg-black/50 rounded-full text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
        aria-label="Go Back"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-5 w-5"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M15 19l-7-7 7-7"
          />
        </svg>
      </button>
      {/* Settings Menu Modal */}
      {isSettingsOpen && (
        <div
          className="absolute inset-0 z-40 flex items-center justify-center bg-black/80 p-4 animate-fade-in backdrop-blur-sm"
          onClick={() => setIsSettingsOpen(false)}
        >
          {" "}
          <form
            onSubmit={handleSettingsSubmit}
            className="bg-gray-800 p-6 rounded-lg shadow-xl w-full max-w-sm text-white space-y-6"
            onClick={(e) => e.stopPropagation()}
          >
            {" "}
            <div className="flex justify-between items-center border-b border-gray-700 pb-3">
              <h3 className="text-xl font-semibold">Settings</h3>
              <button
                type="button"
                onClick={() => setIsSettingsOpen(false)}
                className="p-1 text-gray-400 hover:text-white rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
                aria-label="Close Settings"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>{" "}
            <div>
              <label
                htmlFor="subreddit"
                className="block text-sm font-medium text-gray-300 mb-1"
              >
                Subreddit
              </label>
              <input
                type="text"
                name="subreddit"
                id="subreddit"
                defaultValue={subreddit}
                placeholder="e.g., pics, videos"
                className="w-full px-3 py-1.5 bg-gray-700 border border-gray-600 rounded text-white text-sm focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>{" "}
            <div className="flex gap-4">
              <div className="flex-1">
                <label
                  htmlFor="sort"
                  className="block text-sm font-medium text-gray-300 mb-1"
                >
                  Sort By
                </label>
                <select
                  name="sort"
                  id="sort"
                  defaultValue={sort}
                  className="w-full px-2 py-1.5 bg-gray-700 border border-gray-600 rounded text-white text-sm focus:ring-indigo-500 focus:border-indigo-500 appearance-none"
                >
                  <option value="hot">Hot</option>
                  <option value="new">New</option>
                  <option value="top">Top</option>
                  <option value="rising">Rising</option>
                </select>
              </div>
              <div className="flex-1">
                <label
                  htmlFor="timeframe"
                  className="block text-sm font-medium text-gray-300 mb-1"
                >
                  Timeframe (Top)
                </label>
                <select
                  name="timeframe"
                  id="timeframe"
                  defaultValue={timeframe}
                  disabled={sort !== "top"}
                  className="w-full px-2 py-1.5 bg-gray-700 border border-gray-600 rounded text-white text-sm focus:ring-indigo-500 focus:border-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed appearance-none"
                >
                  <option value="hour">Hour</option>
                  <option value="day">Day</option>
                  <option value="week">Week</option>
                  <option value="month">Month</option>
                  <option value="year">Year</option>
                  <option value="all">All Time</option>
                </select>
              </div>
            </div>{" "}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Show Content Types
              </label>
              <div className="space-y-2">
                <div className="flex items-center">
                  <input
                    id="showVideos"
                    name="showVideos"
                    type="checkbox"
                    defaultChecked={filters.showVideos}
                    className="h-4 w-4 rounded border-gray-500 bg-gray-600 text-indigo-500 focus:ring-indigo-600 cursor-pointer"
                  />
                  <label
                    htmlFor="showVideos"
                    className="ml-2 block text-sm text-gray-300 cursor-pointer"
                  >
                    Videos
                  </label>
                </div>
                <div className="flex items-center">
                  <input
                    id="showImages"
                    name="showImages"
                    type="checkbox"
                    defaultChecked={filters.showImages}
                    className="h-4 w-4 rounded border-gray-500 bg-gray-600 text-indigo-500 focus:ring-indigo-600 cursor-pointer"
                  />
                  <label
                    htmlFor="showImages"
                    className="ml-2 block text-sm text-gray-300 cursor-pointer"
                  >
                    Images & Galleries
                  </label>
                </div>
                <div className="flex items-center">
                  <input
                    id="showEmbeds"
                    name="showEmbeds"
                    type="checkbox"
                    defaultChecked={filters.showEmbeds}
                    className="h-4 w-4 rounded border-gray-500 bg-gray-600 text-indigo-500 focus:ring-indigo-600 cursor-pointer"
                  />
                  <label
                    htmlFor="showEmbeds"
                    className="ml-2 block text-sm text-gray-300 cursor-pointer"
                  >
                    Embeds (Gfycat, YT, etc.)
                  </label>
                </div>
              </div>
            </div>{" "}
            <button
              type="submit"
              className="w-full px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded text-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-800 focus:ring-indigo-500 transition-colors duration-150"
            >
              Apply & Reload Feed
            </button>{" "}
          </form>{" "}
        </div>
      )}
    </div>
  );
};

export default SecretTikTokPage;
